import { useEffect, useRef, useState } from 'react'

import { EMPTY_FUNCTION, EMPTY_STRING } from '../../constants/common'
import { useAsyncCallback } from '../useAsyncCallback'
import { useIsMountedRef } from '../useIsMountedRef'
import { useNotification } from '../useNotification'
import { useTableConfigLoader } from '../useTableConfigLoader'

import { updateOldApiListTableConfig } from './useOldApiListTable.api'
import {
  getOldApiListPageSizePagination,
  getOldApiListPagination,
  getOldApiListPaginationParams,
  getOldApiListPreviousPagination,
  isOldApiListPageMissing,
  isOldApiListTableKeyShown,
  isOldApiListTableLatestLoad,
  isOldApiListTableLoading,
  updateOldApiListTableItem,
} from './useOldApiListTable.utils'

export function useOldApiListTable({
  entity,
  getList,
  listKey = EMPTY_STRING,
  pagination,
  setPagination,
  onLoad = EMPTY_FUNCTION,
}) {
  const [items, setItems] = useState(null)
  const [isLoadingPage, setLoadingPage] = useState(false)
  const [isInitiated, setInitiated] = useState(false)
  const [isPageLoadFailed, setPageLoadFailed] = useState(false)
  const [isConfigLoadFailed, setConfigLoadFailed] = useState(false)

  const loadPageIdRef = useRef(0)
  const shownListKeyRef = useRef(null)

  const isMountedRef = useIsMountedRef()

  const { handleAddErrorNotification } = useNotification()

  const { columns, handleLoadTableConfig, handleSetColumns, requiredColumns } =
    useTableConfigLoader({ entity })

  function isLatestLoadPage(loadPageId) {
    return isOldApiListTableLatestLoad({
      isMounted: isMountedRef.current,
      latestLoadPageId: loadPageIdRef.current,
      loadPageId,
    })
  }

  function finishLoadPage() {
    setLoadingPage(false)
    setInitiated(true)
  }

  function handleLoadPageFail() {
    if (
      isOldApiListTableKeyShown({
        items,
        listKey,
        shownListKey: shownListKeyRef.current,
      })
    ) {
      handleAddErrorNotification('Error.CommonApiRequestFailKey')
    } else {
      setItems(null)
      setPageLoadFailed(true)
    }

    finishLoadPage()
  }

  function handleLoadPageError(error) {
    if (isOldApiListPageMissing({ error, pagination })) {
      setPagination(getOldApiListPreviousPagination(pagination), {
        replace: true,
      })
    } else {
      handleLoadPageFail()
    }
  }

  // Delayed callers (a sync success, mass actions) must load the current
  // page, so the latest closure is called. `false` skips the "Refreshed" toast.
  const handleLoadPage = useAsyncCallback(() => {
    loadPageIdRef.current += 1

    const loadPageId = loadPageIdRef.current

    setLoadingPage(true)

    return getList(getOldApiListPaginationParams(pagination))
      .then(({ data }) => {
        if (isLatestLoadPage(loadPageId)) {
          shownListKeyRef.current = listKey
          setItems(data.results.objects)
          setPageLoadFailed(false)
          setPagination(
            getOldApiListPagination({ meta: data.meta, pagination }),
            {
              replace: true,
            }
          )
          finishLoadPage()
          onLoad(data)
        }

        return data
      })
      .catch(error => {
        if (isLatestLoadPage(loadPageId)) {
          handleLoadPageError(error)
        }

        return false
      })
  })

  function handleChangePageSize(pageSize) {
    setPagination(getOldApiListPageSizePagination({ pageSize, pagination }))
  }

  async function handleLoadConfig() {
    return handleLoadTableConfig().then(config => {
      setConfigLoadFailed(!config)
    })
  }

  async function handleReload() {
    if (isConfigLoadFailed) {
      setConfigLoadFailed(false)
      handleLoadConfig()
    }

    return handleLoadPage()
  }

  useEffect(() => {
    handleLoadConfig()
  }, [])

  useEffect(() => {
    handleLoadPage()
  }, [listKey, pagination.page, pagination.pageSize])

  async function handleApplyConfig(config) {
    return updateOldApiListTableConfig({
      data: { columns, ...config },
      entity,
    })
  }

  function handleUpdateItem({ id, updates }) {
    setItems(prevItems =>
      updateOldApiListTableItem({
        id,
        items: prevItems,
        updates,
      })
    )
  }

  const isLoadFailed = isPageLoadFailed || isConfigLoadFailed
  const isRefetching = isInitiated && isLoadingPage

  return {
    columns,
    handleApplyConfig,
    handleChangePageSize,
    handleReload,
    handleSetColumns,
    handleUpdateItem,
    isLoadFailed,
    isLoading: isOldApiListTableLoading({ columns, isInitiated, isLoadFailed }),
    isRefetching,
    // Rows without a config cannot be drawn, so the load error is shown instead
    items: isConfigLoadFailed ? null : items,
    requiredColumns,
  }
}
